import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/toaster';
import InstanceSyncInitializer from './components/sync/InstanceSyncInitializer';
import './index.css';
import './App.css';
import './styles/chat-layout-protection.css';

// Componentes de Layout (não lazy - necessários imediatamente)
import MainLayout from './components/layout/MainLayout';

// Páginas críticas (não lazy - necessárias imediatamente)
import Dashboard from './pages/Dashboard';

// Páginas com Lazy Loading
const Login = React.lazy(() => import('./pages/Login'));
const ConversationMemoryPage = React.lazy(() => import('./pages/ConversationMemoryPage'));
const ConfiguracaoClinicas = React.lazy(() => import('./pages/configuracoes/Clinicas'));
const ConfiguracaoInstancias = React.lazy(() => import('./pages/configuracoes/Instancias'));
const ConfiguracoesArquivos = React.lazy(() => import('./pages/configuracoes/Arquivos'));
const Doutores = React.lazy(() => import('./pages/Doutores'));
const DoutorForm = React.lazy(() => import('./pages/DoutorForm'));
const Atendentes = React.lazy(() => import('./pages/Atendentes'));

// Componentes com Lazy Loading
const KanbanBoard = React.lazy(() => import('./components/kanban/KanbanBoard'));
const LeadsList = React.lazy(() => import('./components/leads/LeadsList'));
const AppointmentsList = React.lazy(() => import('./components/appointments/AppointmentsList'));


// Componente de Loading para Suspense
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

// Componente para inicializar serviços após autenticação
const AuthenticatedServicesInitializer = () => {
  const { authState } = useAuth();

  useEffect(() => {
    // Só inicializa se o usuário estiver autenticado e não estiver em loading
    if (authState.user && !authState.loading) {
      let cleanupFunction: (() => void) | undefined;

      // Importação dinâmica do ReminderService apenas quando autenticado
      import('./services/ReminderService').then(({ default: reminderService }) => {
        reminderService.startReminderService().catch(error => {
          // Silenciar erros de autenticação durante inicialização
          if (!error.message?.includes('Sessão') && !error.message?.includes('sessão')) {
            console.error('Erro ao iniciar serviço de lembretes:', error);
          }
        });

        cleanupFunction = () => {
          reminderService.stopReminderService();
        };
      });

      return () => {
        if (cleanupFunction) {
          cleanupFunction();
        }
      };
    }
  }, [authState.user, authState.loading]);

  return null;
};

// Componente para rotas protegidas que requerem autenticação
const ProtectedRoute = () => {
  const { authState } = useAuth();
  
  if (authState.loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return authState.user ? (
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </MainLayout>
  ) : (
    <Navigate to="/login" replace />
  );
};

// Componente para redirecionar usuários já autenticados para o dashboard
const PublicRoute = () => {
  const { authState } = useAuth();
  
  if (authState.loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return !authState.user ? (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  ) : (
    <Navigate to="/dashboard" replace />
  );
};

// Componentes página não encontrada
const NotFound = () => {
  const navigate = useNavigate();
  
  return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-primary">404</h1>
        <p className="mt-4 text-xl">Página não encontrada</p>
        <button 
          onClick={() => navigate('/')}
          className="mt-6 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
        >
          Voltar para página inicial
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="sdr-odonto-theme">
      <Router>
        <AuthProvider>
          <AuthenticatedServicesInitializer />
          <InstanceSyncInitializer />
          <div className="min-h-screen flex flex-col w-full m-0 p-0 box-border">
            <Routes>
            {/* Rotas públicas */}
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<Login />} />
            </Route>
            


            {/* Rotas protegidas */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/doutores" element={<Doutores />} />
              <Route path="/doutores/:id" element={<DoutorForm />} />
              <Route path="/atendentes" element={<Atendentes />} />
              <Route path="/leads" element={<LeadsList />} />
              <Route path="/agendamentos" element={<AppointmentsList />} />

              <Route path="/kanban" element={<KanbanBoard />} />

              <Route path="/conversation-memory" element={<ConversationMemoryPage />} />
              <Route path="/conversation-memory/:id" element={<ConversationMemoryPage />} />

              {/* Rotas de configurações */}
              <Route path="/configuracoes/clinicas" element={<ConfiguracaoClinicas />} />
              <Route path="/configuracoes/instancias" element={<ConfiguracaoInstancias />} />
              <Route path="/configuracoes/arquivos" element={<ConfiguracoesArquivos />} />
              
              {/* Redirecionamento da rota antiga de arquivos */}
              <Route path="/arquivos" element={<Navigate to="/configuracoes/arquivos" replace />} />
              
              {/* Rotas legadas para redirecionamento */}
              <Route path="/clinicas" element={<Navigate to="/configuracoes/clinicas" replace />} />
              <Route path="/profissionais" element={<Navigate to="/doutores" replace />} />
            </Route>

            {/* Rota para página não encontrada */}
            <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster />
          </div>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
